/** Composition root: wires HTTP, auth, telemetry, notifications. */
import { AppConfig } from '@core/domain/AppConfig.ts';
import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import { getTenant, setTenant } from '@core/domain/TenantContext.ts';
import type { TenantConfig } from '@core/domain/TenantContext.ts';
import { createHttpClient } from '@core/infrastructure/driven/http/HttpClient.ts';
import type { HttpClient } from '@core/infrastructure/driven/http/HttpClient.ts';
import { AuthService } from '@src/modules/auth/application/AuthService.ts';
import { AuthStorageAdapter } from '@src/modules/auth/infrastructure/driven/AuthStorageAdapter.ts';
import type { AuthOutboundHttpPort } from '@src/modules/auth/domain/ports/AuthOutboundHttpPort.ts';
import type { AuthProviderPort } from '@src/modules/auth/domain/ports/AuthProviderPort.ts';
import type { AuthTokenStoragePort } from '@src/modules/auth/domain/ports/AuthTokenStoragePort.ts';
import { ApiAuthAdapter } from '@src/modules/auth/infrastructure/driven/ApiAuthAdapter.ts';
import type { GetLocalSqliteDatabase, LocalSqliteHealthQuery } from '@src/modules/auth/infrastructure/driven/LocalSqlAuthAdapter.ts';
import { LocalSqlAuthAdapter } from '@src/modules/auth/infrastructure/driven/LocalSqlAuthAdapter.ts';
import { markLocalSqliteDirty } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistDirty.ts';
import { createEntregaStack, createReposicionStack } from './factories/createFeatureStacks.ts';
import { createSystemStack } from './factories/createSystemStack.ts';
import type { LocalSqliteRuntime } from '@src/modules/platform/local-sqlite/domain/LocalSqliteRuntime.ts';
import type { NotificationPort } from '@src/modules/platform/notifications/domain/ports/NotificationPort.ts';
import { DomToastNotificationAdapter } from '@src/modules/platform/notifications/infrastructure/driven/DomToastNotificationAdapter.ts';
import { BrowserSignatureCaptureAdapter } from '@src/modules/platform/signature/infrastructure/driven/BrowserSignatureCaptureAdapter.ts';
import { createTelemetry } from './factories/createTelemetry.ts';
import { SystemApplicationService } from '@src/modules/system/application/SystemApplicationService.ts';
import { EntregaApplicationService } from '@src/modules/entrega-paquetes/application/EntregaApplicationService.ts';
import type { GeolocationPort } from '@src/modules/entrega-paquetes/domain/ports/GeolocationPort.ts';
import type { EntregaMapPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaMapPort.ts';
import type { EntregaPdfPort } from '@src/modules/entrega-paquetes/domain/ports/EntregaPdfPort.ts';
import type { ReposicionPdfPort } from '@src/modules/reposicion-productos/domain/ports/ReposicionPdfPort.ts';
import type { SignatureCapturePort } from '@src/modules/platform/signature/domain/ports/SignatureCapturePort.ts';
import { ReposicionApplicationService } from '@src/modules/reposicion-productos/application/ReposicionApplicationService.ts';

function applyTenantCssVariables(theme: TenantConfig['theme']): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement.style;
  el.setProperty('--color-primary', theme.primaryColor);
  el.setProperty('--color-secondary', theme.secondaryColor);
}

export type AppContainerDeps = {
  localSqlite?: LocalSqliteRuntime;
};

export class AppContainer {
  private readonly httpClient: HttpClient | null;
  private readonly authStorage: AuthStorageAdapter;
  private readonly authService: AuthService;
  private readonly notificationPort: NotificationPort;
  private readonly telemetry: TelemetryPort;
  private readonly systemServices: SystemApplicationService;
  private readonly entregaServices: EntregaApplicationService;
  private readonly reposicionServices: ReposicionApplicationService;
  private readonly geolocationPort: GeolocationPort;
  private readonly entregaMapPort: EntregaMapPort;
  private readonly entregaPdfPort: EntregaPdfPort;
  private readonly reposicionPdfPort: ReposicionPdfPort;
  private readonly signatureCapturePort: SignatureCapturePort;

  constructor(deps: AppContainerDeps = {}) {
    let tenantConfig = AppConfig.getDefaultTenantConfig();
    try {
      tenantConfig = getTenant();
    } catch {
      setTenant(tenantConfig);
    }
    applyTenantCssVariables(tenantConfig.theme);

    const offline = AppConfig.useLocalSqliteClient();
    this.telemetry = createTelemetry();
    this.authStorage = new AuthStorageAdapter(AppConfig.getTokenKey());
    this.httpClient = offline ? null : createHttpClient(this.authStorage, this.telemetry);

    this.authService = createAuthApplicationService(
      this.authStorage,
      offline ? null : this.httpClient,
      offline,
      offline ? () => deps.localSqlite?.getDb() : undefined,
      offline ? () => deps.localSqlite?.getConnectionHealth() : undefined,
    );

    this.notificationPort = DomToastNotificationAdapter.getInstance();

    const getDb = () => deps.localSqlite?.getDb();
    const flushPersist = deps.localSqlite ? () => deps.localSqlite!.flushPersist() : undefined;
    const markDirty = deps.localSqlite ? () => markLocalSqliteDirty() : undefined;
    const getToken = () => this.authStorage.getToken();

    const entregaStack = createEntregaStack({ getDb, getToken, flushPersist, markDirty, auth: this.authService });
    this.entregaServices = entregaStack.service;
    this.geolocationPort = entregaStack.geolocation;
    this.entregaMapPort = entregaStack.entregaMap;
    this.entregaPdfPort = entregaStack.entregaPdf;

    const reposicionStack = createReposicionStack({ getDb, getToken, flushPersist, markDirty, auth: this.authService });
    this.reposicionServices = reposicionStack.service;
    this.reposicionPdfPort = reposicionStack.reposicionPdf;
    this.signatureCapturePort = new BrowserSignatureCaptureAdapter();

    this.systemServices = createSystemStack({
      getDb,
      getToken,
      telemetry: this.telemetry,
      entrega: this.entregaServices,
      reposicion: this.reposicionServices,
      flushPersist,
    });
  }

  getAuthService(): AuthService {
    return this.authService;
  }

  getNotificationPort(): NotificationPort {
    return this.notificationPort;
  }

  getTelemetry(): TelemetryPort {
    return this.telemetry;
  }

  getSystemServices(): SystemApplicationService {
    return this.systemServices;
  }

  getEntregaServices(): EntregaApplicationService {
    return this.entregaServices;
  }

  getReposicionServices(): ReposicionApplicationService {
    return this.reposicionServices;
  }

  getGeolocationPort(): GeolocationPort {
    return this.geolocationPort;
  }

  getEntregaMapPort(): EntregaMapPort {
    return this.entregaMapPort;
  }

  getEntregaPdfPort(): EntregaPdfPort {
    return this.entregaPdfPort;
  }

  getReposicionPdfPort(): ReposicionPdfPort {
    return this.reposicionPdfPort;
  }

  getSignatureCapturePort(): SignatureCapturePort {
    return this.signatureCapturePort;
  }

  getHttpClient(): HttpClient {
    if (!this.httpClient) {
      throw new Error('HTTP no disponible: la app está en modo SQLite local (login y datos sin API).');
    }
    return this.httpClient;
  }
}

const defaultLocalSqlite: GetLocalSqliteDatabase = () => undefined;

function buildAuthProvider(
  tokenStorage: AuthTokenStoragePort,
  http: AuthOutboundHttpPort | null,
  offlineSqlite: boolean,
  getLocalSqlite?: GetLocalSqliteDatabase,
  getSqliteHealth?: LocalSqliteHealthQuery,
): AuthProviderPort {
  if (offlineSqlite) {
    return new LocalSqlAuthAdapter(
      tokenStorage,
      getLocalSqlite ?? defaultLocalSqlite,
      getSqliteHealth,
    );
  }
  if (!http) throw new Error('HttpClient requerido sin SQLite local.');
  return new ApiAuthAdapter(http, tokenStorage);
}

export function createAuthApplicationService(
  tokenStorage: AuthTokenStoragePort,
  http: AuthOutboundHttpPort | null,
  offlineSqlite: boolean,
  getLocalSqlite?: GetLocalSqliteDatabase,
  getSqliteHealth?: LocalSqliteHealthQuery,
): AuthService {
  return new AuthService(
    buildAuthProvider(tokenStorage, http, offlineSqlite, getLocalSqlite, getSqliteHealth),
    tokenStorage,
  );
}
